//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// chrome-day imports
import { IProps, IState, IContainer } from "./ChromeDay.d";
import * as styles from "./ChromeDay.styles";
import { fragments } from "./ChromeDay.gql";
import { ChromeDayPicker } from "./chrome-day-picker";
import { ChromeDayAllDayEvents } from "./chrome-day-all-day-events";
// other components
import { H2 } from "@ui";
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";
import {
  CALENDAR_HEIGHT,
  CalendarWeek,
  CalendarDayAugmentedEvent,
} from "@clockwise/web-commons/src/components/calendar";
// state
import { IReduxState } from "@reducers/root.reducer";
// util
import { page, PageEvents, track, TrackingEvents } from "@util/analytics.util";
import { getCurrentOrg } from "@util/org.util";
import { ZonedMoment } from "@util/time-zone.util";
import { isOnWorkForecasting } from "@util/experiment.util";
import { logger } from "@util/logger.util";
// lodash
import debounce from "lodash/debounce";
// actions
import { setActiveViewEvent } from "@actions/chrome-extension.actions";
import { fromGlobalId } from "@util/graphql.util";
import {
  setSelectedMoment,
  setScrollIsLatched,
  setScrollOffset,
  setSnapToSelectedMomentOnNextMount,
} from "@actions/day-view.actions";

//////////////////
// COMPONENT
//////////////////
export class ChromeDayCmpt extends React.Component<IProps, IState> {
  public static INTERVAL = 60000;

  private _ignoreScroll = false;
  private _interval?: any;
  private _expectedStepTime?: any;
  private _scrolledContainer: HTMLElement | null;
  // ~-~-~-~-~-~-~-
  // Setup
  // ~-~-~-~-~-~-~-
  constructor(props: IProps) {
    super(props);

    this.state = {
      currentTime: new ZonedMoment(),
    };
  }

  private onMinuteChange = () => {
    const { INTERVAL } = ChromeDayCmpt;

    const now = new ZonedMoment();
    let dt = +now - this._expectedStepTime;

    if (dt > INTERVAL) {
      dt = dt % INTERVAL;
    }

    const isToday = now.isSame(this.props.selectedMoment, "days");

    // update the redux state if the time has changed
    if (!now.isSame(this.state.currentTime, "days") && this.props.scrollIsLatched) {
      this.props.setSelectedMoment(now);
      setTimeout(this.scrollToNow, 0);
    } else if (isToday && this.props.scrollIsLatched) {
      setTimeout(this.scrollToNow, 0);
    }

    this.setState({
      currentTime: now,
    });

    this._interval = setTimeout(() => this.onMinuteChange(), INTERVAL - dt);
  };

  private getCurrentTimeTopRatio = (time: ZonedMoment) => {
    return (time.hours() * 60 + time.minutes()) / (24 * 60);
  };

  private setScroll = (scrollOffset: number) => {
    if (this._scrolledContainer) {
      this._ignoreScroll = true;
      this._scrolledContainer.scrollTop = scrollOffset;
      this.props.setScrollOffset(scrollOffset);
    }
  };

  private setScrollToPercentage = (percentage: number) => {
    if (this._scrolledContainer) {
      this.setScroll(percentage * CALENDAR_HEIGHT - this._scrolledContainer.offsetHeight / 3 + 51); // 51 to account for bottom padding
    }
  };

  private scrollToOffset = () => {
    if (this._scrolledContainer) {
      this.setScroll(this.props.scrollOffset);
    }
  };

  private scrollToNow = () => {
    this.props.setScrollIsLatched(true);

    this.setScrollToPercentage(this.getCurrentTimeTopRatio(this.state.currentTime));
  };

  private scrollToSelectedMoment = () => {
    this.setScrollToPercentage(this.getCurrentTimeTopRatio(this.props.selectedMoment));

    this.props.setSnapToSelectedMomentOnNextMount(false);
  };

  private onScroll = () => {
    if (this._scrolledContainer && !this._ignoreScroll) {
      this.props.setScrollOffset(this._scrolledContainer.scrollTop);
      this.props.setScrollIsLatched(false);
    }

    this._ignoreScroll = false;
  };

  private onClick = (calendarId: string, externalEventId: string) => {
    logger.debug("Setting ActiveViewEvent from ChromeDay", { externalEventId, calendarId });

    this.props.setActiveViewEvent(externalEventId, calendarId, false);
    track(TrackingEvents.DAY_VIEW.CLICK_EVENT);
  };

  // ~-~-~-~-~-~-~-
  // lifecycle
  // ~-~-~-~-~-~-~-
  public componentWillUnmount = () => {
    if (this._interval) {
      clearInterval(this._interval);
    }
  };

  public componentDidMount = () => {
    const now = new ZonedMoment();
    const driftTime = ChromeDayCmpt.INTERVAL - now.seconds() * 1000 + now.milliseconds();
    this._expectedStepTime = now.add(driftTime, "milliseconds");

    this._interval = setTimeout(() => this.onMinuteChange(), driftTime);

    // scroll so that the current time is centered
    if (!this.props.loading) {
      const isToday = this.state.currentTime.isSame(this.props.selectedMoment, "days");

      if (isToday && this.props.scrollIsLatched) {
        this.scrollToNow();
      } else if (this.props.snapToSelectedMomentOnNextMount) {
        this.scrollToSelectedMoment();
      } else {
        this.scrollToOffset();
      }
    }

    page(PageEvents.CHROME_DAY_VIEW);
  };

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps.loading && !this.props.loading) {
      const isToday = this.state.currentTime.isSame(this.props.selectedMoment, "days");

      if (isToday && this.props.scrollIsLatched) {
        this.scrollToNow();
      } else if (this.props.snapToSelectedMomentOnNextMount) {
        this.scrollToSelectedMoment();
      } else {
        this.scrollToOffset();
      }
    }
  }

  ///////////////
  // Rendering //
  ///////////////

  /**
   * Render the day view, feed scheduler, and day picker
   *
   * @private
   * @memberof ChromeDayCmpt
   */
  private renderDayView = () => {
    const { selectedMoment, viewer, weekStartDates } = this.props;
    const isToday = this.state.currentTime.isSame(this.props.selectedMoment, "days");

    let maybeRender = null;

    let org = null;

    if (viewer && viewer.user) {
      org = getCurrentOrg(viewer);
    }

    const epbwbwIndex = weekStartDates.indexOf(
      selectedMoment.clone().startOf("week").format("YYYY-MM-DD"),
    );

    const epbwbw =
      org &&
      org.eventParentBaseWrapperByWeeksObject &&
      org.eventParentBaseWrapperByWeeksObject.__typename !== "GraphEntityError" &&
      org.eventParentBaseWrapperByWeeksObject.list[epbwbwIndex];

    const eventParentBases = (epbwbw && epbwbw.eventParentBases) || [];

    const eventParentBaseMap: { [key: string]: number } = {};

    eventParentBases.forEach(
      (eventParentBase) =>
        (eventParentBaseMap[fromGlobalId(eventParentBase.id).id] = eventParentBase.timestamp),
    );

    let events =
      (epbwbw &&
        epbwbw.eventParentsByDay[selectedMoment.day()].eventParents
          .filter((ep) => ep.eventParentBaseTimestamp >= eventParentBaseMap[ep.eventParentBaseId])
          .reduce((prev, curr) => prev.concat(curr.events), [] as ISchema.IEvent[])
          .filter((event) => {
            if (event === undefined || event.locked) {
              return false;
            }

            let youAttendee: ISchema.IAttendee | undefined;

            if (
              event.organizer &&
              this.props.calendarIds.indexOf(event.organizer.urnValue) !== -1
            ) {
              youAttendee = event.organizer;
            } else if (event.attendees) {
              youAttendee = event.attendees.find(
                (attendee) => this.props.calendarIds.indexOf(attendee.urnValue) !== -1,
              );
            }

            const dayStart = selectedMoment.clone().hour(0).minute(0);
            const dayEnd = selectedMoment.clone().hour(23).minute(59);

            let isSameDay = false;

            if (event.isAllDay) {
              const localZonedMoment = new ZonedMoment(selectedMoment.format("YYYY-MM-DD"));
              isSameDay =
                localZonedMoment.isSameOrAfter(event.startTime.dateOrDateTime) &&
                localZonedMoment.isBefore(event.endTime.dateOrDateTime);
            } else {
              isSameDay =
                new ZonedMoment(event.startTime.dateOrDateTime).isBetween(dayStart, dayEnd) ||
                new ZonedMoment(event.endTime.dateOrDateTime).isBetween(dayStart, dayEnd);
            }

            return youAttendee && youAttendee.responseStatus !== "Declined" && isSameDay;
          })) ||
      ([] as ISchema.IEvent[]);

    if (viewer && org && epbwbw) {
      if (!isOnWorkForecasting()) {
        events = events.filter(
          (event) =>
            !(
              event &&
              event.annotatedEvent &&
              event.annotatedEvent.orgTags &&
              event.annotatedEvent.orgTags.find((ot) => ot.state && ot.state.value === "Pain")
            ),
        );
      }

      const { daySettings } = org.userWorkingHours.workingHours;

      const allDayEvents: CalendarDayAugmentedEvent[] = [];
      const normalEvents: CalendarDayAugmentedEvent[] = [];

      events.forEach((event) => {
        if (event.isAllDay) {
          allDayEvents.push({ event });
        } else {
          normalEvents.push({ event });
        }
      });

      let colorSettings: ISchema.IEventColoringSettings | undefined = undefined;
      if (
        org.eventColoringSettingsErrorable &&
        org.eventColoringSettingsErrorable.__typename === "EventColoringSettings"
      ) {
        colorSettings = org.eventColoringSettingsErrorable;
      }

      maybeRender = (
        <div style={styles.mainContainer}>
          <ChromeDayAllDayEvents
            selectedMoment={selectedMoment}
            userId={viewer.user.id}
            events={allDayEvents}
            calendarIds={this.props.calendarIds}
            colorSettings={colorSettings}
            onDayEventClick={this.onClick}
          />
          <div
            style={styles.scrollContainer}
            ref={(c) => (this._scrolledContainer = c)}
            onScroll={debounce(this.onScroll, 100)}
          >
            <CalendarWeek
              mode="standard"
              hideDayHeaders
              showWorkingHours
              userId={viewer.user.id}
              onClick={this.onClick}
              onClickDay={() => null}
              eventsByDay={[
                {
                  normalEvents,
                  allDayEvents,
                  allDayEventsMaxCount: allDayEvents.length,
                  day: selectedMoment,
                },
              ]}
              calendarIds={this.props.calendarIds}
              colorSettings={colorSettings}
              showHoursBorder={false}
              showShortHours={true}
              containerStyle={{ padding: "24px 2px" }}
              hourTextStyle={{ right: 2, fontSize: "11px" }}
              hourTextContainerStyle={{ width: 40 }}
              horizontalGridStyle={{ left: 44 }}
              dayContainerStyle={{ paddingLeft: 8 }}
              daySettings={daySettings as any}
            />
          </div>
        </div>
      );
    }

    let calendarFocusTimeScore = null;
    if (
      viewer &&
      org &&
      org.calendarTimeStatsList &&
      org.calendarTimeStatsList.__typename !== "GraphEntityError" &&
      org.calendarTimeStatsList.list
    ) {
      const o = org;
      const calendarTimeStats = org.calendarTimeStatsList.list.find(
        (cts) => cts.calendarId === o.primaryOrgEmail,
      );
      if (
        calendarTimeStats &&
        calendarTimeStats.scoresByDate &&
        calendarTimeStats.scoresByDate.length
      ) {
        calendarFocusTimeScore =
          calendarTimeStats.scoresByDate.find(
            (cfts) => cfts.secondaryKey === selectedMoment.format("YYYY-MM-DD"),
          ) || null;
      }
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", userSelect: "none" }}>
        <ChromeDayPicker
          org={org}
          calendarFocusTimeScore={calendarFocusTimeScore}
          isToday={isToday}
          selectedMoment={this.props.selectedMoment}
          setSelectedMoment={this.props.setSelectedMoment}
          loading={!!this.props.loading}
          scrollToNow={() => this.scrollToNow()}
        />
        {!this.props.loading ? (
          maybeRender
        ) : (
          <div style={styles.scrollContainerBackground}>
            <CircularProgress />
          </div>
        )}
      </div>
    );
  };

  public render() {
    // check for data requirements
    const { fastCrawlFinished } = this.props;

    if (this.props.loading || (!this.props.loading && fastCrawlFinished)) {
      return this.renderDayView();
    } else {
      return (
        <div style={styles.fastCrawlHeaderContainer}>
          <H2 style={styles.fastCrawlHeader}>Still processing your calendar...</H2>
        </div>
      );
    }
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    selectedMoment: state.dayView.selectedMoment,
    snapToSelectedMomentOnNextMount: state.dayView.snapToSelectedMomentOnNextMount,
    scrollOffset: state.dayView.scrollOffset,
    scrollIsLatched: state.dayView.scrollIsLatched,
  };
}

const reduxContainer = connect(mapStateToProps, {
  setSelectedMoment,
  setScrollOffset,
  setScrollIsLatched,
  setSnapToSelectedMomentOnNextMount,
  setActiveViewEvent,
})(ChromeDayCmpt);

//////////////////
// RELAY
//////////////////
export const ChromeDay = createFragmentContainer(reduxContainer, fragments);
